import { useTheme } from '@emotion/react'
import {
    DataLayerEventName,
} from '@news-mono/web-common'
import React, { useEffect, useRef, useState } from 'react'
import {
    GalleryImages,
    numPerPage,
} from '../../content/Gallery/components/GalleryImages'
import { GalleryThumbnails } from '../../content/Gallery/components/GalleryThumbnails'
import {
    StyledGallery,
    StyledGalleryContentWrapper,
    StyledGallerySharingContainer,
    StyledHeroCaptionContainer,
    StyledHeroWrap,
    StyledTitle,
    StyledTNGalleryBoxWrapper,
    StyledTNGalleryButton,
    StyledTNGalleryContainer,
} from '../../content/Gallery/Gallery.styled'
import { Skeleton } from '../../content/Skeleton/Skeleton'
import { ArticleBlockContent } from '../../publication/ArticleBlockContent/ArticleBlockContent'
import { StyledFigcaption } from '../../publication/ArticleImage/ArticleImage.styled'
import { Byline } from '../../publication/Byline/Byline'
import {
    StyledNestedGrid,
} from '../../templates/Publication/TheNightlyPublication/TheNightlyPublication.styled'
import { smoothScrollTo } from '../../__helpers/smoothscroll'
import { themedValue } from '../../__styling/themed-value'
import { ResponsivePictureLayout } from '../Picture'
import { ResponsivePicture } from '../Picture/Picture'
import { PicturePlaceholder } from '../Picture/PicturePlaceholder'
import { GalleryProps } from './Gallery'
import { IconGalleryTN } from '../../icons/IconGalleryTN/IconGalleryTN'
import { Sharing } from '../../buttons/TheNightlySharing/Sharing'
import { TopicList } from '../TopicList/TopicList'
import { maxSecondLevel } from '../../templates/Publication/lib/max-second-level'

export const TNGallery: React.FC<GalleryProps> = (props) => {
    const [showThumbnails, setShowThumbnails] = useState(false)
    const [totalLoaded, setTotalLoaded] = useState(() => {
        if (props.loading || props.images.length > 0) {
            return numPerPage
        }
        return 0
    })
    const theme = useTheme()

    function loadMoreImages(numToLoad = numPerPage) {
        // If we are loading, assume we will get enough to fill the first page
        // TODO This could hurt viewability if an ad is loaded, then we remove it due to
        // less images in gallery?
        if (props.loading) {
            setTotalLoaded(numToLoad)
        } else if (totalLoaded < props.images.length) {
            setTotalLoaded(totalLoaded + numToLoad)
        }
    }

    useEffect(
        () => {
            if (props.loading) return

            setTotalLoaded(Math.min(props.images.length, numPerPage))
        },
        // Ignore props.images.length changing, the value is only relevant when props.loading becomes true
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [props.loading],
    )

    function toggleThumbnails() {
        if (showThumbnails) {
            props.onEvent({
                type: DataLayerEventName.galleryThumbnailsShow,
                originator: 'Gallery',
                payload: {},
            })
        } else {
            props.onEvent({
                type: DataLayerEventName.galleryThumbnailsHide,
                originator: 'Gallery',
                payload: {},
            })
        }
        setShowThumbnails(!showThumbnails)
    }

    async function scrollTo(index: number) {
        if (index + 1 > totalLoaded) {
            await loadMoreImages(index + 1 - totalLoaded)
        }
        const targetElement = document.querySelector(`#image-${index}`)

        if (!targetElement) {
            return
        }

        smoothScrollTo(targetElement)
    }

    function showMoreClicked() {
        props.onEvent({
            type: DataLayerEventName.galleryLoadMore,
            originator: 'Gallery',
            payload: {},
        })
        loadMoreImages()
    }

    function renderLoadMore() {
        return (
            !props.loading &&
            totalLoaded < props.images.length && (
                <StyledTNGalleryButton 
                        variant={'default'}
                        fill={'outline'}
                        color={'primary'}
                        text={'Load More'}
                        action={{
                            type: 'button',
                            onClick: showMoreClicked,
                        }}
                    />
            )
        )
    }

    const { className, onEvent } = props

    const sharingSectionRef = useRef<HTMLDivElement>(null)
    const mainImage = props.loading ? undefined : props.mainImage
    const caption = props.loading ? (
        <StyledHeroCaptionContainer>
            <Skeleton count={2} />
        </StyledHeroCaptionContainer>
    ) : (
        mainImage && (
            <React.Fragment>
                <StyledFigcaption
                    byline={mainImage.byline}
                    captionText={mainImage.captionText}
                    credit={mainImage.credit}
                    kind="image"
                    imageRenderType="gallery"
                />
            </React.Fragment>
        )
    )

    // If there is a 16:9 we want to use that, but sometimes the wire feeds don't have proper crops, so we fallback to original so it displays an image
    const fixedRatioValue =
        !props.loading && props.mainImage && props.mainImage.crops['16:9']
            ? '16:9'
            : 'original'
    const desktopWidth = themedValue(theme, {
        thenightly: 1224,
        fallback: 1224,
    })

    return (
        <StyledGallery className={className}>
            <StyledHeroWrap>
                {props.loading ? (
                    <PicturePlaceholder
                        fixedRatio="16:9"
                        image={undefined}
                    />
                ) : props.mainImage ? (
                    <ResponsivePicture
                        image={props.mainImage}
                        imageWidths={{
                            mobile: '100vw',
                            tablet: '100vw',
                            desktop: `${desktopWidth}px`,
                            fallbackWidth: desktopWidth,
                        }}
                        fixedRatio={fixedRatioValue}
                        layout={ResponsivePictureLayout.ObjectFitContain}
                    />
                ) : null}

            </StyledHeroWrap>

            <StyledHeroWrap>{caption}</StyledHeroWrap>
            
            <StyledGalleryContentWrapper>
                <StyledNestedGrid gapSize='md'>
                    <React.Fragment>
                        <StyledTitle>
                            {props.loading ? <Skeleton /> : props.title}
                        </StyledTitle>
                    </React.Fragment>

                    {!props.loading && (
                        <Byline
                            profile={props.profile}
                            text={props.byline}
                            timestamp={props.publicationDate}
                            source={props.source}
                            isFullWidth={true}
                            url={props.shareUrl}
                            title={props.title}
                            requiredAccess={props.requiredAccess}
                            allowCommenting={false}
                            noLine={true}
                            paddingBottom={true}
                        />
                    )}

                    {!props.loading && (
                        <ArticleBlockContent
                            blocks={props.blocks}
                            onEvent={props.onEvent}
                            log={props.log}
                            adUnitPath={props.adUnitPath}
                            publicationKind={'gallery'}
                        />
                    )}
                </StyledNestedGrid>
                
                {!props.loading && props.entitled && (
                    <StyledTNGalleryBoxWrapper>
                        <StyledTNGalleryButton
                            variant={'default'}
                            fill={'outline'}
                            color={'primary'}
                            text={showThumbnails ? 'Hide Photos' : 'View All Photos'}
                            action={{
                                type: 'button',
                                onClick: toggleThumbnails,
                            }}
                            icon={
                                !showThumbnails ? {
                                    iconPosition: 'right',
                                    IconElement: <IconGalleryTN fillColour="#161616" marginLeft="0.4rem" />,
                                } : undefined
                            }
                        />

                        {showThumbnails &&
                            !props.loading &&
                            props.entitled && (
                                <GalleryThumbnails
                                    images={props.images}
                                    onEvent={onEvent}
                                    scrollTo={scrollTo}
                                    toggleThumbnails={toggleThumbnails}
                                    enforceAspectRatio={'4:3'}
                                />
                        )}
                    </StyledTNGalleryBoxWrapper>
                )}

            </StyledGalleryContentWrapper>

            <GalleryImages
                loading={props.loading}
                totalLoaded={totalLoaded}
                images={!props.loading ? props.images : []}
                adFrequency={props.adFrequency}
                ads={props.ads}
                themeKind={theme.kind}
            />
            {renderLoadMore()}

            {!props.loading && props.primaryTopic && (
                <StyledTNGalleryContainer>
                    <TopicList
                        primaryTopic={maxSecondLevel(props.primaryTopic)}
                        secondaryTopics={props.secondaryTopics}
                    />
                </StyledTNGalleryContainer>
            )}

            {!props.loading && (
                <StyledGallerySharingContainer
                    ref={sharingSectionRef}
                    commentsAllowed={false}
                >
                    <Sharing
                        url={props.shareUrl}
                        text={props.title}
                        onEvent={props.onEvent}
                        shareOptions={[
                            'facebook',
                            'linkedin',
                            'email_tn',
                            'clipboard',
                        ]}
                        originator="publicationShare"
                    />
                </StyledGallerySharingContainer>
            )}
        </StyledGallery>
    )
}
TNGallery.displayName = 'TNGallery'
